import { Router } from '@angular/router';
import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  EventTypes,
  OidcClientNotification,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import {
  distinctUntilChanged,
  filter,
  BehaviorSubject,
  concatMap,
  forkJoin,
  of,
  Subscription,
} from 'rxjs';
import { UserPortalAuthData } from '../interfaces/local-storage-auth-data.interface';
import {
  USER_PORTAL_AUTH_DATA_EMPTY,
  USER_PORTAL_AUTH_DATA_ITEM,
} from '../interfaces/consts';
import { LoggerService } from '@shared/services/logger/logger.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements OnDestroy {
  isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  userId: BehaviorSubject<string> = new BehaviorSubject<string>('');
  userName: BehaviorSubject<string> = new BehaviorSubject<string>('');
  fortifyRoles: BehaviorSubject<string | string[]> = new BehaviorSubject<
    string | string[]
  >([]);
  rbsRoles: BehaviorSubject<string | string[]> = new BehaviorSubject<
    string | string[]
  >([]);
  fortifyToken: BehaviorSubject<string> = new BehaviorSubject<string>('');
  userPortalAuthData: UserPortalAuthData = USER_PORTAL_AUTH_DATA_EMPTY;

  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly eventService = inject(PublicEventsService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  // Subscriptions management
  private subscriptions = new Subscription();

  /**
   * Checks if the user possesses the expected role.
   * @param expectedRole The role to check for.
   * @returns A boolean indicating whether the user possesses the expected role.
   */
  evaluateUserRoles(expectedRole: string): boolean {
    // Check if the user's roles include the expected role and return the result
    return this.rbsRoles.value.includes(expectedRole);
  }

  /**
   * Checks the authentication state with the IDP.
   * Returns an observable that emits LoginResponse with auth details.
   * This should be called on app initialization to verify authentication.
   */
  checkAuth() {
    return this.oidcSecurityService.checkAuth();
  }

  /**
   * Checks the authentication state and initializes the application.
   * This method should be called on app initialization.
   * It performs the following:
   * - Checks authentication with the IDP
   * - Updates authentication state
   * - Retrieves user data from Fortify
   * - Initializes event listeners for token refresh monitoring
   * Note: Token refresh is handled automatically by silentRenew configuration
   */
  checkAuthAndInitialize(): void {
    this.oidcSecurityService.checkAuth().subscribe(() => {
      // Authentication state is now tracked
      // silentRenew in oidc.config.ts handles automatic token refresh
    });
    this.IsAuthenticatedAlert();
    this.getUserDataFromFortify();

    // Initialize event listeners for monitoring (optional - for debugging/logging)
    this.sessionStateFromIDPListener();
    this.tokenExpiredEventListener();
    this.refreshTokenEventListener();
  }

  /**
   * Subscribes to the isAuthenticated$ observable from the OIDC security service
   * and updates the isAuthenticated$ subject accordingly.
   * It also logs a message indicating whether the user is authenticated or not.
   */
  IsAuthenticatedAlert(): void {
    const authSubscription =
      this.oidcSecurityService.isAuthenticated$.subscribe((response) => {
        if (response.isAuthenticated) {
          this.logger.logStyledIfNotProd('IS AUTH', 'green');
          this.isAuthenticated$.next(true);
        } else {
          this.logger.logStyledIfNotProd('IS NOT AUTH', 'red');
          this.isAuthenticated$.next(false);
        }
      });

    this.subscriptions.add(authSubscription);
  }

  getUserDataFromFortify(): void {
    const userDataSubscription = this.isAuthenticated$
      .pipe(
        concatMap((validSession) => {
          if (validSession) {
            return forkJoin({
              idTokenPayload: this.oidcSecurityService.getPayloadFromIdToken(),
              accessTokenPayload:
                this.oidcSecurityService.getPayloadFromAccessToken(),
            });
          } else {
            return of(null);
          }
        }),
      )
      .subscribe({
        next: (response) => {
          this.userId.next(response?.idTokenPayload.sub);
          this.fortifyRoles.next(
            response?.idTokenPayload.role || 'NO FORTIFY ROLE',
          );
        },
      });

    this.subscriptions.add(userDataSubscription);
  }

  /**
   * Listens for session state changes emitted from the identity provider.
   * When the session state changes to 'changed', it triggers a logout action.
   */
  sessionStateFromIDPListener(): void {
    const sessionSubscription = this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) =>
            notification.type === EventTypes.CheckSessionReceived,
        ),
        distinctUntilChanged((prev, curr) => prev.value === curr.value),
      )
      .subscribe((notification: OidcClientNotification<string>) => {
        this.logger.logIfNotProd(
          'CheckSessionReceived with value from app',
          notification,
        );
        if (notification.value == 'changed') {
          this.logger.logStyledIfNotProd('Should logout', 'red');
          this.logout();
          this.router.navigateByUrl('/');
        }
      });

    this.subscriptions.add(sessionSubscription);
  }

  /**
   * Listens for token expiration events emitted from the identity provider.
   * When a token expiration event is received, it logs the event information.
   */
  tokenExpiredEventListener(): void {
    const tokenExpiredSubscription = this.eventService
      .registerForEvents()
      .pipe(
        filter((notification) => notification.type === EventTypes.TokenExpired),
      )
      .subscribe((notification: OidcClientNotification<string>) => {
        this.logger.logIfNotProd(
          'TokenExpired with value from app',
          notification,
          'warn',
        );
        if (notification.value === 'changed') {
          this.logger.logStyledIfNotProd('NEW ACCESS TOKEN', 'blue');
        }
      });

    this.subscriptions.add(tokenExpiredSubscription);
  }

  /**
   * Listens for silent renew events emitted from the identity provider.
   * When a silent renew event is received, it logs the event information.
   */
  refreshTokenEventListener(): void {
    const refreshEventSubscription = this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) => notification.type === EventTypes.SilentRenewStarted,
        ),
      )
      .subscribe((notification: OidcClientNotification<string>) => {
        this.logger.logIfNotProd(
          'Refresh Token with value from app',
          notification,
          'warn',
        );
        if (notification.value === 'changed') {
          this.logger.logStyledIfNotProd('NEW ACCESS TOKEN', 'blue');
        }
      });

    this.subscriptions.add(refreshEventSubscription);
  }

  /**
   * Returns the item with the authentication data
   *
   * @returns
   */
  getAuthDataFromLocalStorage(): UserPortalAuthData {
    const userPortalAuthData: string | null = localStorage.getItem(
      USER_PORTAL_AUTH_DATA_ITEM,
    );

    if (!userPortalAuthData) {
      return USER_PORTAL_AUTH_DATA_EMPTY;
    }

    const userPortalAuthDataParsed: UserPortalAuthData =
      JSON.parse(userPortalAuthData);

    return userPortalAuthDataParsed;
  }

  /**
   * Sets the the object in the User Portal Authentication Data in the
   * local storage
   *
   * @param newAuthData
   */
  setNewAuthenticationDataObjectInLocalStorage(
    newAuthData: UserPortalAuthData,
  ): void {
    localStorage.setItem(
      USER_PORTAL_AUTH_DATA_ITEM,
      JSON.stringify(newAuthData),
    );
  }

  /**
   * Handles the login callback process after authentication.
   * Waits for authentication to complete and then navigates to the dashboard.
   * @returns A subscription that should be unsubscribed in ngOnDestroy
   */
  handleLoginCallback() {
    return this.oidcSecurityService.isAuthenticated$
      .pipe(
        filter(
          (isAuthenticatedResult) => isAuthenticatedResult.isAuthenticated,
        ),
      )
      .subscribe({
        next: () => {
          this.router.navigateByUrl('/dashboard/projects/list');
        },
      });
  }

  login(): void {
    this.oidcSecurityService.authorize();
  }

  logout(): void {
    this.cleanupSubscriptions();

    this.oidcSecurityService.logoff().subscribe({
      next: () => {
        const userPortalAuthData = this.getAuthDataFromLocalStorage();
        userPortalAuthData.expirationDate = null;
        userPortalAuthData.sessionStatedAt = null;
        userPortalAuthData.sessionEndedAt = new Date();
        this.setNewAuthenticationDataObjectInLocalStorage(userPortalAuthData);
        sessionStorage.clear();
        localStorage.clear();
      },
      error: (error) => {
        this.logger.logIfNotProd('Error logging out:', error, 'error');
      },
    });
  }

  /**
   * Cleans up all subscriptions to prevent memory leaks.
   */
  private cleanupSubscriptions(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
  }

  /**
   * Lifecycle hook that is called when the service is destroyed.
   * Cleans up all subscriptions and intervals.
   */
  ngOnDestroy(): void {
    this.cleanupSubscriptions();
  }
}
